import React from 'react';
import PropTypes from 'prop-types';

import Browser from '@wetransfer/concorde-browser';

import { debounceAsync } from '@wetransfer/concorde-debounce';

import { TransferAPI } from '@app/transfer-bundle/services/api/transfer';
import Actions from '@app/store/action';
import Store from '@app/store';

import I18n from '@module/i18n';
import {
  canBeShownAsGrid,
  getItemType,
  itemHeight,
} from '@app/transfer-bundle/helpers/preview';

import Action from '@component/preview/action';
import Audio from '@component/preview/file-types/audio';
import Icon from '@component/icon';
import Image from '@component/preview/file-types/image';
import LazyLoad from '@component/lazyload';
import Loader from '@component/loader';
import Preloader from '@component/preview/preloader';

import { WHITE } from '@constant/colors';
import { TransferDownloadActions } from '@app/transfer-bundle/store/download-actions';

export default class FileWrapper extends React.Component {
  static propTypes = {
    action: PropTypes.object,
    autoResize: PropTypes.bool,
    className: PropTypes.string,
    errorAction: PropTypes.bool,
    item: PropTypes.object,
    loadingIndicator: PropTypes.node,
    onClick: PropTypes.func,
    route: PropTypes.object,
    scrollEl: PropTypes.object,
  };

  static defaultProps = {
    autoResize: true,
    loadingIndicator: <Loader backgroundColor="transparent" />,
    errorAction: true,
  };

  state = {
    loading: true,
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.fileTypes = {
      image: ['JPG', 'JPEG'],
      audio: ['MP3'],
    };

    this.getPreview = debounceAsync(this.getPreview.bind(this), 500);
    this.onDownload = this.onDownload.bind(this);
    this.onError = this.onError.bind(this);
    this.onLoad = this.onLoad.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.item.file.id !== prevProps.item.file.id &&
      this.props.item.file.previewable &&
      !this.props.item.file.state.error
    ) {
      this.setState({ loading: true });
      this.getPreview();
    }

    const {
      item: { file },
    } = this.props;

    if (file.state.loaded && this.state.loading) {
      this.setState({ loading: false });
    }
  }

  /**
   * Calculates the dimensions of the node using its offsetWidth & -Height
   * @return {Object} Node dimensions
   */
  getNodeDimensions() {
    if (!this.node) {
      return null;
    }

    const width = Math.round(this.node.offsetWidth);
    const height = Browser.isMobile
      ? 999999
      : Math.round(this.node.offsetHeight);

    return {
      height,
      width,
    };
  }

  async getPreview() {
    if (
      !this.props.item.file.previewable ||
      this.props.item.file.state.loaded
    ) {
      return;
    }

    const {
      transfer,
      route: {
        params: { secret },
      },
    } = Store.getState();

    try {
      const nodeDimensions = this.getNodeDimensions();
      const { file_url } = await TransferAPI.requestPreview(
        transfer.id,
        secret,
        transfer.recipient_id,
        this.props.item.file.id,
        transfer.password,
        `${nodeDimensions.width}x${nodeDimensions.height}`
      );
      Store.dispatch(
        Actions.Transfer.previewLoading(this.props.item.file.id, file_url)
      );
    } catch (e) {
      Store.dispatch(Actions.Transfer.previewError(this.props.item.file.id));
    }
  }

  onError() {
    Store.dispatch(Actions.Transfer.previewError(this.props.item.file.id));
    this.setState({ loading: false });
  }

  onLoad() {
    Store.dispatch(Actions.Transfer.previewLoaded(this.props.item.file.id));
  }

  onDownload() {
    const { transfer, session } = Store.getState();

    Store.dispatch(
      TransferDownloadActions.downloadTransferFile({
        transfer,
        secret: transfer.secret,
        recipientId: transfer.recipient_id,
        fileId: this.props.route.params.file_id,
        domainUserId: session.domainUserId,
      })
    );
  }

  onClick = () => {
    const { onClick, item } = this.props;

    if (typeof onClick === 'function') {
      onClick(item);
    }
  };

  /**
   * Which component to render based on the type
   * @param  {Object} file File description provided by backend
   * @param  {String} type File type (image, audio...)
   * @return {Node}        File type component
   */
  getFileComponent(file, type) {
    if (file.state.error || !file.previewable) {
      type = 'unsupported';
    }

    switch (type) {
      case 'image':
        return <Image src={file.previewUri} onClick={this.onClick} />;
      case 'audio':
        return <Audio file={file} />;
      default:
        return (
          <Icon className="preview__item--icon" type="Fallback" color={WHITE} />
        );
    }
  }

  generateStyles(type, metadata) {
    const nodeDimensions = this.getNodeDimensions();
    const height = itemHeight[type]
      ? itemHeight[type](nodeDimensions, metadata || {})
      : 0;
    return {
      height: this.props.autoResize && this.state.loading ? height : 'inherit',
      backgroundColor:
        metadata && metadata.main_color ? metadata.main_color : 'transparent',
    };
  }

  render() {
    const {
      item: { file },
      autoResize,
      className,
    } = this.props;
    const {
      transfer: { items },
    } = Store.getState();
    const type = getItemType(file);
    const containerClassnames = ['preview__container'];
    let style = {};
    const fileComponent = this.getFileComponent(file, type);
    const action = {
      text: I18n.t('transfer.download_preview'),
      callback: this.onDownload,
      ...this.props.action,
    };
    const actionBarClassnames = [];
    let content = fileComponent;

    if (className) {
      containerClassnames.push(className);
    }

    if (!file.state.loaded && file.previewable) {
      content = (
        <Preloader
          loadingIndicator={this.props.loadingIndicator}
          type={type}
          media={file.previewUri}
          onLoad={this.onLoad}
          onError={this.onError}
          ready={!this.state.loading}
        >
          {fileComponent}
        </Preloader>
      );
    }

    if (
      (file && !file.previewable) ||
      (file && file.state && file.state.error)
    ) {
      actionBarClassnames.push('preview__bar-notsupported');
    }

    if (canBeShownAsGrid(items)) {
      containerClassnames.push('preview__container--grid');
    }

    if (file && file.previewable) {
      style = this.generateStyles(type, file.ref.metadata);
    }

    return (
      <div
        ref={(node) => (this.node = node)}
        style={{ opacity: this.node || !autoResize ? 1 : 0 }}
        className={containerClassnames.join(' ')}
      >
        <LazyLoad
          {...style}
          onVisible={this.getPreview}
          enabled={!file.state.loaded}
          scrollEl={this.props.scrollEl}
        >
          {content}
        </LazyLoad>
        <Action
          title={file.name}
          subTitle={I18n.size(file.size)}
          action={action}
          className={actionBarClassnames.join(' ')}
          icon
        />
      </div>
    );
  }
}
